import { zodToEffectSchema } from "@redotech/effect-api-shared-common/zod";
import { ConfirmCancelOrderSchema } from "@redotech/redo-model/agents/support-agents/confirm-cancel-order";
import { ConfirmCreateBundleSchema } from "@redotech/redo-model/agents/support-agents/confirm-create-bundle";
import { ConfirmResendReturnLabelSchema } from "@redotech/redo-model/agents/support-agents/confirm-resend-return-label";
import { SubscribeBackInStockConfirmationSchema } from "@redotech/redo-model/agents/support-agents/confirm-subscribe-to-back-in-stock";
import { ConfirmUpdateOrderSchema } from "@redotech/redo-model/agents/support-agents/confirm-update-order";
import { ShippingAddressToUpdateSchema } from "@redotech/redo-model/agents/support-agents/confirm-update-order-shipping-address";
import { ConfirmUploadedImageForProductVisualizationSchema } from "@redotech/redo-model/agents/support-agents/confirm-uploaded-image-for-product-visualization";
import { ContentType } from "@redotech/redo-model/conversation";
import { Schema } from "effect";
import * as z from "zod/v4";
import { ConversationId } from "../ids/definitions";
import { WidgetConversationResponseEffectSchema } from "../models/shopper-ai/conversation/widget-conversation";

/**
 * Discriminated union for customer-driven tool-call confirmations sent
 * from the chat widget. Keyed on the same `ContentType` values used by
 * `RedoChatMessage` — one arm per tool-call UI component the AI can
 * emit. Each arm carries:
 *
 *  - `conversationId` + `pendingToolCallId` — the message being confirmed
 *  - `confirmationData` — the v1 tool-specific Zod schema extended with
 *    the boolean flag that drives confirm-vs-reject (`confirmUpdate`,
 *    `confirmCancel`, etc.) or implicit confirm (`resendReturnLabel`,
 *    `subscribeToBackInStock`, `confirmCustomerSignIn`,
 *    `confirmProductVisualization` — always confirm, no cancel path).
 *
 * Payload shapes come straight from v1 Zod schemas via
 * `zodToEffectSchema` so the contract stays 1:1 with the existing
 * customer-widget-rpc handlers.
 */
export const ConfirmPendingToolCallInput = Schema.Union(
  Schema.Struct({
    confirmationType: Schema.Literal(ContentType.UPDATE_ORDER),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: zodToEffectSchema(
      ConfirmUpdateOrderSchema.extend({ confirmUpdate: z.boolean() }),
    ),
  }),
  Schema.Struct({
    confirmationType: Schema.Literal(ContentType.CANCEL_ORDER),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: zodToEffectSchema(
      ConfirmCancelOrderSchema.required().extend({
        confirmCancel: z.boolean(),
      }),
    ),
  }),
  Schema.Struct({
    confirmationType: Schema.Literal(ContentType.UPDATE_ADDRESS),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: zodToEffectSchema(
      ShippingAddressToUpdateSchema.extend({
        confirmed: z.boolean().optional(),
        cancelled: z.boolean().optional(),
      }),
    ),
  }),
  Schema.Struct({
    confirmationType: Schema.Literal(ContentType.CREATE_BUNDLE),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: zodToEffectSchema(
      ConfirmCreateBundleSchema.extend({ confirmCreateBundle: z.boolean() }),
    ),
  }),
  Schema.Struct({
    confirmationType: Schema.Literal(ContentType.RESEND_RETURN_LABEL_UI),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: zodToEffectSchema(ConfirmResendReturnLabelSchema),
  }),
  Schema.Struct({
    confirmationType: Schema.Literal(
      ContentType.SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION_UI,
    ),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: zodToEffectSchema(SubscribeBackInStockConfirmationSchema),
  }),
  Schema.Struct({
    confirmationType: Schema.Literal(ContentType.CUSTOMER_SIGN_IN_UI),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: Schema.Struct({ email: Schema.String }),
  }),
  Schema.Struct({
    confirmationType: Schema.Literal(
      ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI,
    ),
    conversationId: ConversationId,
    pendingToolCallId: Schema.String,
    confirmationData: zodToEffectSchema(
      ConfirmUploadedImageForProductVisualizationSchema,
    ),
  }),
);
export type ConfirmPendingToolCallInput =
  typeof ConfirmPendingToolCallInput.Type;

/**
 * Response returned to the widget after a confirmation is applied.
 * Includes the full refreshed conversation so the widget can render the
 * resolved pending-tool-call message (new `confirmationState`) plus any
 * AI-continuation turns the confirmation kicked off. Progressive deltas
 * still arrive via the widget-conversations SSE channel — this is the
 * authoritative fail-safe.
 */
export const ConfirmPendingToolCallResponse = Schema.Struct({
  conversation: WidgetConversationResponseEffectSchema,
});
export type ConfirmPendingToolCallResponse =
  typeof ConfirmPendingToolCallResponse.Type;
